// Root.tsx
import {Outlet, useLocation} from "react-router-dom";
import Footer from "../components/common/Footer";
import ScrollToTop from "../util/ScrollToTop";
import React, {useEffect} from "react";
import {useCookies} from "react-cookie";
import CookieBanner from "../components/common/CookieBanner";
import UserHeader from "../components/user/UserHeader";
import EmailVerificationBanner from "../components/user/products/EmailVerificationBanner";
import {TraceService} from "../service/TraceService";
import {TraceType} from "../model/user/general/Trace";
import {DiagnosticService} from "../service/DiagnosticService";
import {setFirstJoinedFromIfNotExists} from "../util/TrackingUtil";
import {useUser} from "../context/UserContext";
import {lazyLoadFacebookPixel, lazyLoadIntercom} from "../util/LazyLoadScripts";
import {MathJaxContext} from "better-react-mathjax";
import useGoogleAdsPageView from "../hooks/useGoogleAdsPageView";

// Paths where header and footer should be hidden
const hiddenHeaderFooterPaths = [
    // Learning plan exercise page - matches paths like /produkt/planer-nauki/{id}/{date}/{index}
    /^\/produkt\/planer-nauki\/[a-zA-Z0-9]{1,}\/\d{4}-\d{2}-\d{2}\/\d+$/,

    // V3 onboarding flow — focused, no nav distractions
    /^\/start\/[^/]+$/,
    /^\/spotkanie\/[^/]+$/,

    // KED sales page — Slow Lane, no header/footer distractions
    /^\/oferta\/kurs-efektywnego-dzialania$/,

    // KED Fast Lane pages — newsletter + post-webinar purchase, same no-chrome treatment
    /^\/newsletter\/kurs-efektywnego-dzialania$/,
    /^\/newsletter\/dzien-dziecka$/,
    /^\/webinar\/zakup\/[^/]+$/,
    /^\/webinar\/zakup-raty\/[^/]+$/,

    // KED teen self-check page — direct-to-teen, no chrome
    /^\/kurs-efektywnego-dzialania\/czy-to-dla-mnie$/,

    // Produkacja OS platforma — teen-targeted, full immersion
    /^\/platforma$/,
];

interface RootLayoutProps {
    customHeader?: JSX.Element,
    customFooter?: JSX.Element,
}

export default function RootLayout({customHeader, customFooter}: RootLayoutProps) {
    const [cookies, setCookie] = useCookies(["cookieConsent"]);
    const location = useLocation();
    const shouldLoadMathjax = location.pathname.startsWith('/produkt/');
    const {user} = useUser();

    // Track PageView events for Google Ads
    useGoogleAdsPageView();

    // Function to check if current path should have header/footer hidden
    const shouldHideHeaderFooter = () => {
        const currentPath = location.pathname;
        return hiddenHeaderFooterPaths.some(pattern => pattern.test(currentPath));
    };

    useEffect(() => {
        // Add global error handler for MathJax errors
        const handleGlobalError = (event: ErrorEvent) => {
            if (event.message && (
                event.message.includes('Typesetting failed') ||
                event.message.includes('nextSibling') ||
                event.message.includes('MathJax') ||
                (event.message.includes('Cannot read properties') && event.message.includes('null'))
            )) {
                // Prevent the error from showing in the console as an uncaught error
                event.preventDefault();
                console.debug('MathJax rendering error caught globally:', event.message);
                return true;
            }
        };

        window.addEventListener('error', handleGlobalError);

        return () => {
            window.removeEventListener('error', handleGlobalError);
        };
    }, []);

    useEffect(() => {
        if (user && user.intercomToken) {
            // Lazy load Intercom when user data is available
            lazyLoadIntercom('cdnbbgwq', {
                intercom_user_jwt: user.intercomToken,
                name: user.name,
                created_at: Math.floor(new Date(user.createdAt).getTime() / 1000),
            });
        }
    }, [user]); // React to user changes

    useEffect(() => {
        // Lazy load Facebook Pixel after initial render
        const initializeFbPixel = async () => {
            try {
                const ReactPixel = await lazyLoadFacebookPixel();

                const fbPixelId = process.env.REACT_APP_FB_PIXEL_ID;
                if (cookies.cookieConsent) {
                    loadGoogleAnalytics();
                    ReactPixel.init(fbPixelId, undefined, {debug: false, autoConfig: true});
                    ReactPixel.grantConsent();
                    ReactPixel.pageView();
                    setFirstJoinedFromIfCookieConsent();
                } else {
                    ReactPixel.init(fbPixelId, undefined, {debug: false, autoConfig: true});
                    ReactPixel.revokeConsent();
                    ReactPixel.pageView();
                }
            } catch (error) {
                const errorMessage = error instanceof Error
                    ? `${error.name}: ${error.message}\n${error.stack}`
                    : String(error);
                DiagnosticService.addDiagnostic({
                    fullEvent: {},
                    error: errorMessage,
                    functionName: 'useEffect Root'
                })
            }
        };

        // Delay Facebook Pixel initialization slightly to prioritize critical resources
        setTimeout(initializeFbPixel, 100);
    }, [cookies]);

    function addCookieCookiesConsent(consent: boolean) {
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        setCookie("cookieConsent", consent, {path: "/", expires: oneYearFromNow});
        TraceService.addTrace(TraceType.CookieConsent, JSON.stringify({consent: consent}));
    }

    function giveCookieConsent() {
        try {
            addCookieCookiesConsent(true);
            loadGoogleAnalytics();
            // Clarity.consent(true);
            setFirstJoinedFromIfCookieConsent();
        } catch (error) {
            DiagnosticService.addDiagnostic({fullEvent: {}, error: error, functionName: 'giveCookieConsent Root'})
        }

    }

    function rejectCookieConsent() {
        addCookieCookiesConsent(false);
    }

    function loadGoogleAnalytics() {
        try {
            // @ts-ignore
            gtag('consent', 'update', {
                'ad_storage': 'granted',
                'ad_user_data': 'granted',
                'ad_personalization': 'granted',
                'analytics_storage': 'granted'
            });
        } catch (e) {
            DiagnosticService.addDiagnostic({
                functionName: "cookie-consent",
                error: e,
                fullEvent: {},
                additionalInfo: 'error while granting consent for google ads'
            })
        }
    }

    async function activateFbPixel() {
        const ReactPixel = await lazyLoadFacebookPixel();
        ReactPixel.init(process.env.REACT_APP_FB_PIXEL_ID, undefined, {debug: false, autoConfig: true});
        ReactPixel.grantConsent();
        ReactPixel.pageView();
    }

    function setFirstJoinedFromIfCookieConsent() {
        const urlParams = new URLSearchParams(window.location.search);
        const source = urlParams.get("source") ?? "direct";
        setFirstJoinedFromIfNotExists(source);
    }

    // MathJax configuration for the entire app
    const mathJaxConfig = {
        loader: {load: ['[tex]/ams', '[tex]/cancel', '[tex]/noerrors', '[tex]/noundefined']},
        tex: {
            packages: {'[+]': ['ams', 'cancel', 'noerrors', 'noundefined']},
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$', '$$'], ['\\[', '\\]']],
            processEscapes: true,
            processEnvironments: true
        },
        options: {
            renderActions: {
                addMenu: []  // Disable right-click menu
            },
            skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code']
        },
        startup: {
            typeset: false  // Disable automatic typesetting on page load
        }
    };

    const content = (
        <>
            <ScrollToTop/>
            {
                !shouldHideHeaderFooter() && (customHeader ? customHeader : <UserHeader/>)
            }
            {
                !shouldHideHeaderFooter() && user && !window.location.pathname.startsWith('/weryfikuj-email') &&
                <EmailVerificationBanner user={user}/>
            }
            <Outlet/>
            {
                !shouldHideHeaderFooter() && (customFooter ? customFooter : <Footer/>)
            }
            {(cookies.cookieConsent === undefined || cookies.cookieConsent === null) &&
                <CookieBanner giveCookieConsent={giveCookieConsent} rejectCookieConsent={rejectCookieConsent}/>}
        </>
    );

    // Wrap with MathJaxContext only when needed
    if (shouldLoadMathjax) {
        return (
            <MathJaxContext
                version={3}
                config={mathJaxConfig}
                onError={(error: Error | undefined) => {
                    // Silently ignore DOM-related errors from React re-renders
                    if (error?.message && (
                        error.message.includes('nextSibling') ||
                        error.message.includes('contains') ||
                        error.message.includes('null') ||
                        error.message.includes('undefined') ||
                        error.message.includes('Typesetting failed') ||
                        error.message.includes('Cannot read properties')
                    )) {
                        // These are usually harmless errors from React re-renders
                        console.debug('MathJax DOM error (ignored):', error.message);
                        return;
                    }
                    console.error('MathJax error:', error);
                }}
            >
                {content}
            </MathJaxContext>
        );
    }

    return content;
}
